import axios from "axios";

export function isRequestAbortError(cause: unknown): boolean {
  return (
    axios.isCancel(cause) ||
    (cause instanceof Error &&
      (cause.name === "AbortError" || cause.name === "CanceledError"))
  );
}

/**
 * Every shape our own cancellation can take — the predicate a `catch` block
 * needs before it decides a failure was real.
 *
 * `isRequestAbortError` recognizes only the clean path (a `CanceledError`, or a
 * DOM `AbortError`). Two more shapes reach a fetcher after an abort:
 *
 * 1. An `AxiosError` coded `ERR_CANCELED` that is not a `CanceledError`
 *    instance — no `__CANCEL__` and named `AxiosError`, so `axios.isCancel`
 *    misses it.
 * 2. A bare `ERR_NETWORK`. Axios' XHR adapter guards `request.onerror` and the
 *    abort listener on the same handle and nulls it after whichever fires first
 *    (axios 1.18.0 `lib/adapters/xhr.js`), so a browser teardown in the same
 *    tick as our cancellation rejects as a network failure. Only the request's
 *    own signal tells the two apart.
 *
 * Case 2 gates on the absence of a response: a server that answered produced a
 * real HTTP failure the abort merely followed (a 5xx the caller unmounted on is
 * still a 5xx).
 *
 * Deliberately a separate export rather than a widening of
 * `isRequestAbortError`: that predicate has ~40 call sites branching on it for
 * user-facing recovery, and broadening it there would change behavior well
 * outside cancellation handling.
 */
export function isCancelledRequestError(cause: unknown): boolean {
  if (isRequestAbortError(cause)) return true;
  if (!axios.isAxiosError(cause)) return false;
  if (cause.code === "ERR_CANCELED") return true;
  return cause.response === undefined && cause.config?.signal?.aborted === true;
}
